import { WORD_LENGTH } from "@mk/shared";

/** Aktif satır: dolu olmayan hücreler `undefined`. */
export type DraftCells = readonly (string | undefined)[];

export interface DraftState {
  cells: DraftCells;
  /**
   * Oyuncunun elle seçtiği kutucuk.
   *
   * Seçiliyken yazılan her harf O kutucuğu değiştirir ve imleç orada kalır:
   * "beş harften yalnız ikincisini bulamadım" durumunda aynı kutucuğu
   * ard arda deneyebilmek için. Seçim yokken yazım soldan sağa akar.
   */
  pinned: number | null;
}

export function emptyDraft(): DraftState {
  return { cells: Array.from({ length: WORD_LENGTH }, () => undefined), pinned: null };
}

function firstEmpty(cells: DraftCells): number {
  return cells.findIndex((cell) => cell === undefined);
}

function lastFilled(cells: DraftCells): number {
  for (let i = cells.length - 1; i >= 0; i--) {
    if (cells[i] !== undefined) return i;
  }
  return -1;
}

function write(state: DraftState, index: number, value: string | undefined): DraftState {
  const cells = [...state.cells];
  cells[index] = value;
  return { cells, pinned: state.pinned };
}

/** Harf yaz. Seçili kutucuk varsa onu değiştirir, yoksa ilk boşluğa yazar. */
export function typeLetter(state: DraftState, letter: string): DraftState {
  if (state.pinned !== null) return write(state, state.pinned, letter);
  const index = firstEmpty(state.cells);
  return index === -1 ? state : write(state, index, letter);
}

/** Sil. Seçili kutucuk varsa onu boşaltır, yoksa son dolu hücreyi siler. */
export function deleteLetter(state: DraftState): DraftState {
  if (state.pinned !== null) return write(state, state.pinned, undefined);
  const index = lastFilled(state.cells);
  return index === -1 ? state : write(state, index, undefined);
}

/** Kutucuğa basmak seçer; aynı kutucuğa tekrar basmak seçimi bırakır. */
export function selectCell(state: DraftState, index: number): DraftState {
  if (index < 0 || index >= state.cells.length) return state;
  return { cells: state.cells, pinned: state.pinned === index ? null : index };
}

/** Tahmin gönderilebilir mi? Boş hücre varsa null. */
export function draftWord(state: DraftState): string | null {
  return state.cells.every((cell) => cell !== undefined)
    ? (state.cells as string[]).join("")
    : null;
}
